import type { NativeStackNavigationOptions } from 'expo-router';
import { useIsFocused } from 'expo-router/react-navigation';
import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  useMemo,
} from 'react';

interface ExtendedScreenOptions extends NativeStackNavigationOptions {
  headerBackgroundContent?: React.ReactNode;
  inlineTabsBar?: React.ReactNode;
  hideTitle?: boolean;
  /**
   * Opts a screen out of the large-screen title-bar back chevron: list roots
   * reachable directly from the left panel (feeds, people, photos, etc.) where
   * back isn't needed. Other subpages leave it unset and get the chevron by
   * default. No-op on web and native mobile, where the browser / MobileHeader
   * handle back navigation.
   */
  hideBackButton?: boolean;
  /**
   * Tells the title bar the back chevron sits over a cover image, so it uses
   * the high-contrast blur-pill treatment instead of the plain chevron. Set it
   * from first render (e.g. profiles) so the style doesn't flip once the cover
   * image finishes loading.
   */
  backButtonOnImage?: boolean;
}

interface ScreenOptionsContextType {
  currentOptions: ExtendedScreenOptions;
  setScreenOptions: (options: ExtendedScreenOptions) => void;
}

const ScreenOptionsContext = createContext<ScreenOptionsContextType | undefined>(undefined);

export function ScreenOptionsProvider({ children }: { children: ReactNode }) {
  const [currentOptions, setCurrentOptions] = useState<ExtendedScreenOptions>({});

  const setScreenOptions = useCallback((options: ExtendedScreenOptions) => {
    setCurrentOptions(options);
  }, []);

  const value = useMemo(
    () => ({ currentOptions, setScreenOptions }),
    [currentOptions, setScreenOptions]
  );

  return <ScreenOptionsContext.Provider value={value}>{children}</ScreenOptionsContext.Provider>;
}

export function useScreenOptions() {
  const context = useContext(ScreenOptionsContext);
  if (!context) {
    throw new Error('useScreenOptions must be used within a ScreenOptionsProvider');
  }
  return context;
}

export function useOptionalScreenOptions() {
  return useContext(ScreenOptionsContext);
}

/**
 * Stores screen options in the provider so consumers (LargeScreenTitleBar,
 * WebDocumentTitle) can read them. Pass `enabled={false}` to skip the state
 * update entirely — callers should disable registration on native mobile,
 * where the native stack header reads its options directly from
 * `Stack.Screen` and the registry has no consumer. Skipping avoids a
 * provider-wide re-render on every focused-screen render during a navigation
 * transition.
 *
 * @param options Screen options to publish on focus.
 * @param enabled Whether to publish; defaults to true for backwards compatibility.
 */
export function useRegisterScreenOptions(options: ExtendedScreenOptions, enabled: boolean = true) {
  const { setScreenOptions } = useScreenOptions();
  const isFocused = useIsFocused();

  useEffect(() => {
    if (enabled && isFocused) {
      setScreenOptions(options);
    }
  }, [enabled, isFocused, options, setScreenOptions]);
}
